/**
 * Regras de acesso ao Formulario de Entrada 2.0.
 *
 * Ficam fora de `OnboardingGate.tsx` porque aquele arquivo so pode exportar componentes
 * (react-refresh/only-export-components) — e porque as duas guardas descritas la vivem
 * em arquivos diferentes (`OnboardingGate` e `DashboardLayout`) e precisam decidir pelo
 * MESMO predicado. Um predicado duplicado seria o caminho mais curto para o ping-pong
 * de redirecionamento que o gate existe para evitar.
 */
import type { User } from '@/types/user'
import { NO_PLAN_PATH, needsPlan } from '@/lib/planFeatures'

export const ONBOARDING_PATH = '/onboarding'
/** LGPD-05: tela de aceite dos documentos legais. Vem ANTES do onboarding. */
export const LEGAL_ACCEPTANCE_PATH = '/aceite'
/** R12: tela "Assine seu contrato" para quem ja concluiu o onboarding sem assinar. */
export const CONTRACT_PATH = '/contrato'
/** Configuracoes do aluno. */
export const SETTINGS_PATH = '/app/configuracoes'
/** MH13-20 + R15-12: ancora da Privacidade, onde fica o "Contato por WhatsApp". */
export const SETTINGS_PRIVACY_PATH = `${SETTINGS_PATH}#privacidade`

const ROLE_HOME_PATH: Record<User['role'], string> = {
  admin: '/admin/dashboard',
  instructor: '/instructor/dashboard',
  student: '/app/dashboard',
}

/** Para onde mandar quem nao precisa (ou nao pode) responder o formulario. */
export function homePathForRole(user: User | null): string {
  return user ? ROLE_HOME_PATH[user.role] : ROLE_HOME_PATH.student
}

/**
 * So o aluno que ainda nao concluiu e barrado. Admin e instrutor nunca respondem o
 * formulario — mesma politica de `User#profile_incomplete?`, que faz `return false unless student?`.
 *
 * O `!== true` e proposital: um `user` persistido no localStorage antes deste campo existir
 * vem sem a chave, e o conservador e tratar como "ainda nao respondeu" ate o `/auth/me` dizer
 * o contrario.
 */
export function needsOnboarding(user: User | null): boolean {
  if (!user || user.role !== 'student') return false
  return user.onboarding_completed !== true
}

/**
 * MH13-20: a UNICA rota de `/app` que o aluno sem onboarding ainda abre — Configuracoes,
 * onde o aluno recem-chegado (inclusive sem plano) permite ou nega o contato por WhatsApp,
 * na secao Privacidade (R15-12).
 * So a pagina em si (sem subrotas): nada mais de `/app` escapa do onboarding.
 *
 * O ping-pong com o `OnboardingGate` continua impossivel: esta isencao so evita a ida
 * para `/onboarding`, nunca manda para la.
 */
export function isOpenWithoutOnboarding(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === SETTINGS_PATH
}

/**
 * LGPD-05: vale para TODOS os papeis — admin e instrutor tambem tem conta e dados na
 * plataforma. `pending_legal_documents` vem do `/auth/me`; um `user` persistido antes
 * de o campo existir vem sem a chave, e aqui o conservador e o oposto do onboarding:
 * sem a lista nao ha o que aceitar, e o proximo `/auth/me` corrige. Barrar um admin
 * por causa de um localStorage velho seria pior que deixar passar por um render.
 */
export function needsLegalAcceptance(user: User | null): boolean {
  if (!user) return false
  return (user.pending_legal_documents?.length ?? 0) > 0
}

/**
 * R12: aluno com contrato a assinar nao usa a plataforma. Quem ainda nao concluiu o
 * onboarding assina dentro dele (o `complete` exige), entao aqui so depois do formulario.
 */
export function needsContract(user: User | null): boolean {
  if (!user || user.role !== 'student') return false
  return user.contract_required === true && !needsOnboarding(user)
}

/**
 * Ordem do fluxo pos-login: aceite -> (sem plano? tela "sem plano", r7-25) -> onboarding -> contrato -> home. Quem nao tem pendencia
 * segue para o proximo degrau; e o destino que a tela de aceite usa depois de gravar.
 */
export function nextPathAfterLegal(user: User | null): string {
  if (needsPlan(user)) return NO_PLAN_PATH
  if (needsOnboarding(user)) return ONBOARDING_PATH
  if (needsContract(user)) return CONTRACT_PATH
  return homePathForRole(user)
}
